import { useState } from 'react';
import {
  RiAddCircleLine,
  RiArchive2Line,
  RiFolder3Line,
  RiPriceTag3Line,
  type RemixiconComponentType,
} from '@remixicon/react';
import { Button, ErrorState, Footnote, Group, Icon, Row, SectionLabel, Skeleton } from '@store/ui';
import { api, formatMoney } from '../api';
import { haptic } from '../telegram';
import { fillScreen, useAsync } from './shared';

// --- Profile -----------------------------------------------------------------

const LEDGER_LABELS: Record<string, string> = {
  accrual: 'Начисление',
  spend: 'Списание',
  reservation: 'Резерв',
  reservation_release: 'Возврат резерва',
  expiration: 'Сгорание',
  referral_reward: 'Реферальное начисление',
  promo_reward: 'Бонус по промокоду',
  admin_adjustment: 'Корректировка оператором',
  refund_reversal: 'Отмена при возврате',
};

/* --- the way into store management ------------------------------------------
 *
 * IMPORTANT, and the reason this comment exists: everything below decides what
 * is *drawn*, and nothing below decides what is *allowed*. The real gate is on
 * the server — every one of these routes ends in an admin endpoint that calls
 * `requirePermission(request, …)` against roles read from the database via the
 * verified session, and answers 403 to anyone without them no matter what this
 * component rendered. A customer who types the hash by hand reaches the screen
 * and gets a wall of errors from the API, which is correct.
 *
 * So do not add a check here and consider a new admin route protected. Add it
 * on the server, and mirror it here only so that a customer is not shown a door
 * that will not open for them.
 *
 * Mirrored per row rather than per section: the rows are hidden, never disabled,
 * because a greyed-out «Склад» tells a shop assistant they are missing something
 * without telling them what — and it is one more thing on a phone screen that
 * cannot be tapped.
 */
interface ManageEntry {
  readonly route: string;
  readonly title: string;
  readonly subtitle?: string;
  /** The server permission the screen behind this row actually needs. */
  readonly permission: string;
  readonly Glyph: RemixiconComponentType;
}

const MANAGE_ENTRIES: readonly ManageEntry[] = [
  {
    route: '/admin/products',
    title: 'Товары',
    permission: 'catalog.write',
    Glyph: RiPriceTag3Line,
  },
  {
    route: '/admin/products/new',
    title: 'Добавить товар',
    permission: 'catalog.write',
    Glyph: RiAddCircleLine,
  },
  {
    route: '/admin/categories',
    title: 'Категории',
    permission: 'catalog.write',
    Glyph: RiFolder3Line,
  },
  /*
   * Stock is per product, and the row says so rather than pretending otherwise.
   *
   * There is no store-wide stock screen: the only route is
   * `/admin/products/:id/stock`, reached from a product's «…» menu, because a
   * batch of keys belongs to one product and the importer needs to know which
   * before it can do anything. Sending «Склад» to `/admin/stock` would be a
   * link to nothing — `resolveAdminRoute` returns null for it and the customer
   * would land back on Home — so it opens the list the product is chosen from,
   * and the subtitle is what makes that a step rather than a wrong turn.
   *
   * The permission follows the endpoint it ends at, not the section it sits in:
   * importing stock is `inventory.write` on the server, never `catalog.write`.
   */
  {
    route: '/admin/products',
    title: 'Склад',
    /* `.rowitem__subtitle` truncates on one line, and the sentence this
       replaced was cut off at «остатки грузят…», which says nothing at all.
       Measured in the row rather than counted: 218px of text against the 226
       the row gives it at 360pt and the 256 at 390pt, so it fits whole on every
       phone from 360 up and degrades to a readable «…карточке т…» below that. */
    subtitle: 'Остатки — на карточке товара',
    permission: 'inventory.write',
    Glyph: RiArchive2Line,
  },
];

/**
 * One management row.
 *
 * Hand-assembled from the same `.rowitem` classes `Row` emits, and for one
 * reason: `Row`'s `icon` takes a name from the app's own twenty-glyph set,
 * which has no mark for a category, a warehouse or an add action. The glyph
 * comes from Remix Icon like the tab bar's do; every other pixel of the row —
 * the inset separator, the 28px leading column, the pressed state, the chevron
 * — is the shared row, not a second one.
 */
function ManageRow({ entry, onNavigate }: { entry: ManageEntry; onNavigate: (r: string) => void }) {
  return (
    <button
      type="button"
      className="rowitem rowitem--inset"
      onClick={() => {
        haptic.impact('light');
        onNavigate(entry.route);
      }}
    >
      <span className="rowitem__icon">
        <entry.Glyph size={20} aria-hidden="true" />
      </span>
      <span className="rowitem__body">
        <span className="rowitem__title">{entry.title}</span>
        {entry.subtitle ? <span className="rowitem__subtitle">{entry.subtitle}</span> : null}
      </span>
      <span className="rowitem__chevron">
        <Icon name="chevron" size={17} />
      </span>
    </button>
  );
}

export function ProfileScreen({
  session,
  onNavigate,
}: {
  /* `permissions` is the server's own answer, handed back by `/api/auth/telegram`
     and resolved there from the roles table — never from `initDataUnsafe` and
     never from anything the client could set. Optional so that the screen still
     renders while the session is in flight; absent means "not staff", which is
     the safe way round. */
  session: { user: { firstName: string | null }; permissions?: readonly string[] } | null;
  onNavigate: (route: string) => void;
}) {
  // One request, and every figure in it comes from the server; nothing is
  // derived in the client.
  const profile = useAsync(() => api.profile(), []);
  const [copied, setCopied] = useState(false);

  const stats = profile.data?.stats;
  const balance = profile.data?.balance;
  const referrals = profile.data?.referrals;

  /* An ordinary customer gets no section, no heading and no empty group — the
     list is filtered first and the whole block is skipped when it comes back
     empty, so there is nothing left to hint that a shop lives in here. */
  const granted = session?.permissions;
  const manage = granted ? MANAGE_ENTRIES.filter((e) => granted.includes(e.permission)) : [];

  const copyLink = async () => {
    if (!referrals) return;
    await navigator.clipboard?.writeText(referrals.link).catch(() => {});
    setCopied(true);
    haptic.impact('light');
    setTimeout(() => setCopied(false), 2000);
  };

  /* The name belongs to the heading, not to a surface of its own. It used to be
     a single line of text inside a bordered card directly under a title that
     said "Профиль" — a box drawn around a subtitle. */
  const header = (
    <header>
      <h1 className="title">Профиль</h1>
      <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
        {session?.user.firstName ?? 'Гость'}
      </p>
    </header>
  );

  if (profile.error) {
    return (
      <div className="container stack">
        {header}
        <div style={fillScreen}>
          <ErrorState
            title="Не удалось загрузить профиль"
            text={profile.error}
            onRetry={profile.reload}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container stack">
      {header}

      {profile.loading ? (
        <Skeleton height={56} />
      ) : stats ? (
        <Group padded>
          {/* Two figures, not three. «получено» counted delivered orders — a
              number nobody can read off its own label: received what, and how
              does it differ from «заказов» standing right beside it? Two counts
              of the same thing, told apart only by a status the customer never
              sees, are worse than one. What survives is the pair whose labels
              say what they are, and nothing was invented to fill the space. */}
          <div className="stat-row">
            <div className="stat">
              <span className="stat__value">{stats.spent.map(formatMoney).join(' + ')}</span>
              <span className="stat__label">потрачено</span>
            </div>
            <div className="stat">
              <span className="stat__value">{stats.totalOrders}</span>
              <span className="stat__label">заказов</span>
            </div>
          </div>
        </Group>
      ) : null}

      <section>
        <SectionLabel>Бонусный счёт</SectionLabel>
        {profile.loading ? (
          <Skeleton height={120} />
        ) : balance ? (
          <div className="stack stack--tight">
            <Group>
              <Row
                icon="wallet"
                title="Доступно"
                trailing={<span className="price">{formatMoney(balance.available)}</span>}
              />
              {BigInt(balance.reserved.amount) > 0n ? (
                <Row title="В резерве" trailing={formatMoney(balance.reserved)} />
              ) : null}
            </Group>

            {balance.entries.length > 0 ? (
              <Group>
                {balance.entries.slice(0, 8).map((entry) => (
                  <Row
                    key={entry.id}
                    title={LEDGER_LABELS[entry.type] ?? entry.type}
                    subtitle={new Date(entry.createdAt).toLocaleDateString('ru-RU')}
                    trailing={
                      <span
                        className={
                          entry.amount.amount.startsWith('-') ? 'ledger--out' : 'ledger--in'
                        }
                      >
                        {formatMoney(entry.amount)}
                      </span>
                    }
                  />
                ))}
              </Group>
            ) : null}

            {/* One footnote under the group carries both the empty ledger and
                the disclaimer. Two stacked captions saying quiet things is one
                caption too many. */}
            <Footnote>
              {balance.entries.length > 0
                ? balance.disclaimer
                : `Операций пока нет. ${balance.disclaimer}`}
            </Footnote>
          </div>
        ) : null}
      </section>

      <section>
        <SectionLabel>Реферальная программа</SectionLabel>
        {profile.loading ? (
          <Skeleton height={140} />
        ) : referrals ? (
          <Group padded>
            <p className="text">{referrals.howItWorks}</p>

            <div className="stat-row" style={{ marginTop: 'var(--space-4)' }}>
              <div className="stat">
                <span className="stat__value">{referrals.invited}</span>
                <span className="stat__label">приглашено</span>
              </div>
              <div className="stat">
                <span className="stat__value">{referrals.earned.map(formatMoney).join(' + ')}</span>
                <span className="stat__label">начислено</span>
              </div>
              {referrals.pendingRewards > 0 ? (
                <div className="stat">
                  <span className="stat__value">{referrals.pendingRewards}</span>
                  <span className="stat__label">ожидают</span>
                </div>
              ) : null}
            </div>

            <hr className="divider" />
            <p className="secret">{referrals.link}</p>
            <div style={{ height: 'var(--space-3)' }} />
            <Button variant="secondary" block onClick={copyLink}>
              <Icon name={copied ? 'check' : 'copy'} size={16} />
              {copied ? 'Скопировано' : 'Скопировать ссылку'}
            </Button>
          </Group>
        ) : null}
      </section>

      <Group>
        <Row
          icon="bubble"
          title="Нужна помощь?"
          subtitle="Написать в поддержку"
          chevron
          onClick={() => onNavigate('/support')}
        />
      </Group>

      {/* Last on the screen, deliberately. The owner is a customer here too, and
          their balance, their orders and their referral link are not worth less
          than their shop's back office — so the back office waits its turn at
          the bottom instead of pushing them down the page. */}
      {manage.length > 0 ? (
        <section>
          <SectionLabel>Управление магазином</SectionLabel>
          <div className="stack stack--tight">
            <Group>
              {/* Keyed by title, not route: «Товары» and «Склад» are two ways
                  into the same list and would otherwise collide. */}
              {manage.map((entry) => (
                <ManageRow key={entry.title} entry={entry} onNavigate={onNavigate} />
              ))}
            </Group>
            <Footnote>Изменения видны покупателям сразу после сохранения.</Footnote>
          </div>
        </section>
      ) : null}
    </div>
  );
}
